import { Button, Form, Image, Input } from "antd";
import { DeviceUUID } from "device-uuid";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Loader from "../../../components/loader/loader";
import { auth, db } from "../../../firebase";
import {
  getFirebaseAuthErrorMessage,
  GreenNotify,
  RedNotify,
} from "../../../helper/helper";
import { userData } from "../../../redux/userDataSlice";
import { isAdminUser } from "../../../helper/userDisplay";
import "./login.css";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isloading, setIsLoading] = useState(false);

  const onFinish = async (values) => {
    let deviceId = localStorage.getItem("deviceId");
    if (!deviceId) {
      let id = new DeviceUUID().get();
      localStorage.setItem("deviceId", id);
      deviceId = id;
    }

    setIsLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        values?.email,
        values?.password,
      );

      // Gate access on the Firestore role, not a hardcoded email — any user
      // whose users/{uid} doc has role === "admin" may access the dashboard.
      const userRef = doc(db, "users", userCredential?.user?.uid);
      const res = await getDoc(userRef);

      if (res.exists() && isAdminUser(res.data())) {
        // Record this device, then sign the admin in. Do NOT touch fcmToken:
        // the web panel doesn't use push (it has the in-app /notifications
        // inbox), and `fcmToken` holds the admin's MOBILE FCM token — writing a
        // placeholder here would clobber it and silently kill their phone
        // push notifications until they re-login on mobile.
        const lastLoginAt = new Date().toISOString();
        await updateDoc(userRef, { deviceId: deviceId, lastLoginAt });

        dispatch(userData({ ...res.data(), deviceId, lastLoginAt }));
        GreenNotify("Login Successful");
        navigate("/dashboard", { replace: true });
      } else {
        // Not an admin — don't leave them holding a valid Firebase session.
        await signOut(auth);
        RedNotify("This account doesn't have admin access.");
      }
    } catch (error) {
      // Any failure after a successful sign-in (e.g. the profile read) must not
      // leave a live, persisted session behind.
      await signOut(auth).catch(() => {});
      // Auth-create/sign-in failures carry "auth/" codes; anything else (e.g. a
      // Firestore permission/network error) is clearer via its own message.
      const message = error?.code?.startsWith("auth/")
        ? getFirebaseAuthErrorMessage(error.code)
        : error?.message || "Unable to sign in. Please try again.";
      console.log("Error signing in:", error?.code, error?.message);
      RedNotify(message);
    } finally {
      setIsLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  return (
    <div className="admin-panel-login">
      <div className="login-main-container">
        <Loader loading={isloading} />

        {/* Logo added here */}
        <div style={{ textAlign: "center", marginBottom: "0.2rem" }}>
          <Image
            src={require("../../../assets/icon.png")}
            alt="Company Logo"
            style={{
              height: "90px",
              width: "auto",
              maxWidth: "100%",
            }}
          />
        </div>

        <h1 style={{ marginBottom: "3rem" }}>Login</h1>
        <Form
          name="basic"
          layout="vertical"
          requiredMark={false}
          initialValues={{ remember: true }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
        >
          <Form.Item
            label="Email"
            name="email"
            rules={[
              {
                required: true,
                message: "Please input your Email!",
              },
              {
                type: "email",
                message: "Please enter a valid email address!",
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Password"
            name="password"
            rules={[
              {
                required: true,
                message: "Please input your password!",
              },
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: "0.8rem" }}>
            <Button type="primary" htmlType="submit">
              Sign in
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default Login;
